import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  UserCheck, UserX, Trash2, Users, Clock, Plus, Search, Shield, Phone, Mail,
  Eye, EyeOff, ChevronRight, RefreshCw, AlertTriangle, Check, X, LogIn,
  BarChart3, Target, PhoneCall, Calendar, TrendingUp, FileText, Edit2,
  Power, UserMinus, ArrowRight, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  admin: { label: 'Admin', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  sales_rep: { label: 'Sales Rep', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  front_desk: { label: 'Front Desk', color: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  on_leave: { label: 'On Leave', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  training: { label: 'Training', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  inactive: { label: 'Inactive', color: 'bg-slate-100 text-slate-500 border-slate-200' },
  left_organization: { label: 'Left Org', color: 'bg-red-50 text-red-600 border-red-200' },
};

const UserManagement = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  // Modals
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [tempPassword, setTempPassword] = useState(null); // {name, password}
  const [bulkResetting, setBulkResetting] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [transferSummary, setTransferSummary] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', role: 'sales_rep' });

  const { user, login: authLogin } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const canManage = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    if (!canManage) { toast.error('Access denied'); navigate('/dashboard'); return; }
    fetchAll();
  }, [user]);

  const fetchAll = async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        api.get('/team/agents'),
        isSuperAdmin ? api.get('/admin/pending-requests').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setAllUsers(usersRes.data);
      setPendingRequests(pendingRes.data);
    } catch (e) { logger.error(e); }
  };

  const filteredUsers = allUsers.filter(u => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!u.name?.toLowerCase().includes(q) && !u.phone?.includes(q) && !u.email?.toLowerCase().includes(q)) return false;
    }
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (filterStatus !== 'all' && (u.user_status || 'active') !== filterStatus) return false;
    return true;
  });

  // ── Actions ───────────────────────────────────────
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password) { toast.error('Name, phone & password are required'); return; }
    setLoading(true);
    try {
      const payload = { ...form, phone: form.phone.startsWith('+91') ? form.phone : `+91${form.phone.replace(/\D/g, '')}` };
      if (!payload.email) payload.email = null;
      await api.post('/admin/create-user', payload);
      toast.success('User created');
      setShowAddUser(false);
      setForm({ name: '', phone: '', email: '', password: '', role: 'sales_rep' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setLoading(true);
    try {
      const payload = {};
      if (form.name && form.name !== selectedUser.name) payload.name = form.name;
      if (form.email && form.email !== selectedUser.email) payload.email = form.email;
      if (form.phone) {
        const formatted = form.phone.startsWith('+91') ? form.phone : `+91${form.phone.replace(/\D/g, '')}`;
        if (formatted !== selectedUser.phone) payload.phone = formatted;
      }
      if (isSuperAdmin && form.role && form.role !== selectedUser.role) payload.role = form.role;
      if (form.user_status && form.user_status !== (selectedUser.user_status || 'active')) payload.user_status = form.user_status;

      if (Object.keys(payload).length === 0) { toast.info('No changes'); setLoading(false); return; }
      await api.put(`/admin/update-user/${selectedUser.id}`, payload);
      toast.success('User updated');
      setShowEditUser(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleToggleAssignable = async (u) => {
    try {
      await api.put(`/admin/update-user/${u.id}`, { assignable: !u.assignable });
      toast.success(`${u.name} ${u.assignable ? 'removed from' : 'added to'} assignment list`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async (userId, name) => {
    if (!window.confirm(`Generate a new temporary password for ${name}?`)) return;
    try {
      const res = await api.post(`/admin/reset-user-password/${userId}`);
      // Extract the temp password from the API response message
      const msg = res.data?.message || '';
      const match = msg.match(/Temporary password:\s*(\S+)/);
      const pw = match ? match[1] : null;
      if (pw) {
        setTempPassword({ name, password: pw });
      } else {
        toast.success(`Password reset for ${name}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBulkReset = async () => {
    const nonSA = allUsers.filter(u => u.role !== 'super_admin' && u.user_status !== 'inactive').length;
    if (!window.confirm(`This will reset passwords for ${nonSA} employee accounts to aavaas123 and require each to set a new password on next login.\n\nSuper Admin accounts will NOT be affected.\n\nProceed?`)) return;
    setBulkResetting(true);
    try {
      const res = await api.post('/admin/bulk-reset-passwords', { include_super_admins: false });
      toast.success(`${res.data.count} passwords reset. Employees must change on next login.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBulkResetting(false); }
  };


  const handleApprove = async (userId) => {
    try { await api.post(`/admin/approve-user/${userId}`); toast.success('Approved'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const handleReject = async (userId) => {
    try { await api.post(`/admin/reject-user/${userId}`); toast.success('Rejected'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const openProfile = async (u) => {
    setSelectedUser(u);
    setShowProfile(true);
    try {
      const res = await api.get(`/admin/user-stats/${u.id}`);
      setUserStats(res.data);
    } catch { setUserStats(null); }
  };

  const openEdit = (u) => {
    setSelectedUser(u);
    setForm({ name: u.name, phone: u.phone?.replace('+91', '') || '', email: u.email || '', password: '', role: u.role, user_status: u.user_status || 'active' });
    setShowEditUser(true);
  };

  const openDeactivate = async (u) => {
    setSelectedUser(u);
    setTransferTo('');
    setShowDeactivate(true);
    try {
      const res = await api.get(`/admin/user-transfer-summary/${u.id}`);
      setTransferSummary(res.data);
    } catch { setTransferSummary({ leads: 0, clients: 0, deals: 0, tasks: 0 }); }
  };

  const handleDeactivate = async () => {
    if (!transferTo) { toast.error('Select a user to transfer records to'); return; }
    setLoading(true);
    try {
      await api.post(`/admin/deactivate-user/${selectedUser.id}?transfer_to=${transferTo}`);
      toast.success(`${selectedUser.name} deactivated`);
      setShowDeactivate(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleActivate = async (userId) => {
    try { await api.post(`/admin/activate-user/${userId}`); toast.success('User reactivated'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async () => {
    if (deleteConfirm !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setLoading(true);
    try {
      await api.delete(`/admin/delete-user/${selectedUser.id}`);
      toast.success('User permanently deleted');
      setShowDelete(false);
      setDeleteConfirm('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const handleImpersonate = async (u) => {
    if (!window.confirm(`Log in as ${u.name}? This will be recorded in the audit log.`)) return;
    try {
      const res = await api.post(`/admin/impersonate/${u.id}`);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('originalUser', JSON.stringify(user));
      window.location.href = '/dashboard';
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAuditLog = async () => {
    setShowAudit(true);
    try {
      const res = await api.get('/admin/audit-log?limit=200');
      setAuditLogs(res.data);
    } catch { setAuditLogs([]); }
  };

  const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??';
  const getInitialColor = (role) => {
    const map = { super_admin: 'from-purple-500 to-purple-600', admin: 'from-blue-500 to-blue-600', sales_rep: 'from-emerald-500 to-emerald-600', front_desk: 'from-orange-400 to-orange-500' };
    return map[role] || 'from-slate-400 to-slate-500';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never';

  const activeUsersForTransfer = allUsers.filter(u => (u.user_status || 'active') === 'active' && u.id !== selectedUser?.id);

  const canEditUser = (target) => {
    if (isSuperAdmin) return true;
    if (user?.role === 'admin' && ['sales_rep', 'front_desk'].includes(target.role)) return true;
    return false;
  };

  // ── Role badge / Status badge helpers ──────────────
  const renderRoleBadge = (role) => {
    const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.sales_rep;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.color}`}>{cfg.label}</span>;
  };

  const renderStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.color}`}>{cfg.label}</span>;
  };

  return (
    <Layout>
      <div className="aavaas-page" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="aavaas-page-title" data-testid="user-management-title">User Management</h1>
            <p className="text-sm text-slate-500 mt-1">{allUsers.length} team members</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isSuperAdmin && (
              <Button variant="outline" className="h-9 text-sm rounded-lg border-slate-200" onClick={handleBulkReset} disabled={bulkResetting} data-testid="bulk-reset-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${bulkResetting ? 'animate-spin' : ''}`} /> {bulkResetting ? 'Resetting...' : 'Reset All Passwords'}
              </Button>
            )}
            {isSuperAdmin && (
              <Button variant="outline" className="h-9 text-sm rounded-lg border-slate-200" onClick={openAuditLog} data-testid="audit-log-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Audit Log
              </Button>
            )}
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg shadow-sm h-9 text-sm font-semibold" onClick={() => { setForm({ name: '', phone: '', email: '', password: '', role: 'sales_rep' }); setShowAddUser(true); }} data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add User
            </Button>
          </div>
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> {pendingRequests.length} Pending Approval</h3>
            <div className="space-y-2">
              {pendingRequests.map(r => (
                <div key={r.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.email} — Requested {formatDate(r.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(r.id)} data-testid={`approve-${r.id}`}><Check className="w-3 h-3 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleReject(r.id)} data-testid={`reject-${r.id}`}><X className="w-3 h-3 mr-1" /> Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-3 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, phone, email..." className="pl-10 bg-slate-50 border-slate-200 h-9 text-sm rounded-lg" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="user-search" />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[140px] h-9 text-sm bg-white border-slate-200 rounded-lg shadow-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(ROLE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] h-9 text-sm bg-white border-slate-200 rounded-lg shadow-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* User Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredUsers.map(u => {
            const status = u.user_status || 'active';
            const roleCfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.sales_rep;
            return (
              <div key={u.id} className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden card-hover" data-testid={`user-card-${u.id}`}>
                <div className="p-4">
                  <div className="flex items-start gap-3.5">
                    {/* Avatar */}
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getInitialColor(u.role)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm`}>
                      {getInitials(u.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-sm text-slate-900 truncate">{u.name}</span>
                        {status !== 'active' && renderStatusBadge(status)}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {renderRoleBadge(u.role)}
                        {u.business_vertical && u.business_vertical !== 'general' && (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            u.business_vertical === 'plots' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            u.business_vertical === 'flats' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                            u.business_vertical === 'all' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            'bg-slate-50 text-slate-500 border-slate-200'
                          }`} data-testid={`vertical-${u.id}`}>
                            {u.business_vertical === 'plots' ? 'Plots' : u.business_vertical === 'flats' ? 'Flats' : u.business_vertical === 'all' ? 'All' : u.business_vertical}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        {u.phone && <span className="flex items-center gap-1 font-mono"><Phone className="w-3 h-3" />{u.phone.replace('+91', '')}</span>}
                        {u.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{u.email}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                    <span title="Leads Assigned"><Users className="w-3 h-3 inline mr-0.5" />{u.assigned_leads || 0} leads</span>
                    <span title="Active Deals"><Target className="w-3 h-3 inline mr-0.5" />{u.active_deals || 0} deals</span>
                    <span className="ml-auto" title="Last login"><Clock className="w-3 h-3 inline mr-0.5" />{u.last_login ? formatDateTime(u.last_login) : 'Never'}</span>
                  </div>
                </div>

                {/* Actions Bar */}
                {canEditUser(u) && (
                  <div className="px-4 py-2.5 bg-slate-50/50 border-t border-slate-100 flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-600" onClick={() => openProfile(u)} data-testid={`view-${u.id}`}><Eye className="w-3 h-3" /> View</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-600" onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}><Edit2 className="w-3 h-3" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-600" onClick={() => handleResetPassword(u.id, u.name)} data-testid={`reset-pw-${u.id}`}><RefreshCw className="w-3 h-3" /> Password</Button>
                    {isSuperAdmin && (
                      <Button size="sm" variant="ghost" className={`h-7 text-xs gap-1 ${u.assignable ? 'text-emerald-600' : 'text-slate-400'}`} onClick={() => handleToggleAssignable(u)} title="Show in Assign Agent dropdown" data-testid={`assignable-toggle-${u.id}`}>
                        <UserCheck className="w-3 h-3" /> {u.assignable ? 'Assignable' : 'Not assignable'}
                      </Button>
                    )}
                    {status === 'active' ? (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-amber-600" onClick={() => openDeactivate(u)}><Power className="w-3 h-3" /> Deactivate</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-emerald-600" onClick={() => handleActivate(u.id)}><Power className="w-3 h-3" /> Activate</Button>
                    )}
                    {isSuperAdmin && u.role !== 'super_admin' && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-slate-400 ml-auto" onClick={() => { setSelectedUser(u); setDeleteConfirm(''); setShowDelete(true); }}><Trash2 className="w-3 h-3" /></Button>
                    )}
                    {isSuperAdmin && u.id !== user.id && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-purple-500" onClick={() => handleImpersonate(u)} title="Login as this user"><LogIn className="w-3 h-3" /></Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-16">
            <Users className="w-12 h-12 mx-auto text-slate-200 mb-3" />
            <p className="text-sm text-slate-500 font-medium">No users match your filters</p>
          </div>
        )}

        {/* ══════════ ADD USER DIALOG ══════════ */}
        <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
          <DialogContent className="max-w-md rounded-xl">
            <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-10 rounded-lg" required data-testid="add-user-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone Number *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">+91</span>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="pl-12 h-10 rounded-lg" placeholder="9876543210" maxLength={10} required data-testid="add-user-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email (optional)</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 h-10 rounded-lg" data-testid="add-user-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                    {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                    <SelectItem value="sales_rep">Sales Representative</SelectItem>
                    <SelectItem value="front_desk">Front Desk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password *</Label>
                <div className="relative mt-1">
                  <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="pr-10 h-10 rounded-lg" required data-testid="add-user-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowAddUser(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-add-user">{loading ? 'Creating...' : 'Create User'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ══════════ EDIT USER DIALOG ══════════ */}
        <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
          <DialogContent className="max-w-md rounded-xl">
            <DialogHeader><DialogTitle>Edit {selectedUser?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-10 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">+91</span>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="pl-12 h-10 rounded-lg" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 h-10 rounded-lg" />
              </div>
              {isSuperAdmin && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="sales_rep">Sales Representative</SelectItem>
                      <SelectItem value="front_desk">Front Desk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={form.user_status || 'active'} onValueChange={v => setForm({ ...form, user_status: v })}>
                  <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowEditUser(false)}>Cancel</Button>
                <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800">{loading ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ══════════ USER PROFILE SHEET ══════════ */}
        <Sheet open={showProfile} onOpenChange={setShowProfile}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedUser && (
              <>
                <SheetHeader className="mb-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getInitialColor(selectedUser.role)} flex items-center justify-center text-white text-xl font-bold shadow-md`}>
                      {getInitials(selectedUser.name)}
                    </div>
                    <div>
                      <SheetTitle className="text-xl">{selectedUser.name}</SheetTitle>
                      <div className="flex items-center gap-2 mt-1.5">
                        {renderRoleBadge(selectedUser.role)}
                        {renderStatusBadge(selectedUser.user_status || 'active')}
                      </div>
                    </div>
                  </div>
                </SheetHeader>

                {/* Contact Info */}
                <div className="space-y-2 mb-6">
                  {selectedUser.phone && (
                    <div className="flex items-center gap-2.5 text-sm text-slate-600"><Phone className="w-4 h-4 text-slate-400" /><a href={`tel:${selectedUser.phone}`} className="text-blue-600 font-mono">{selectedUser.phone}</a></div>
                  )}
                  {selectedUser.email && (
                    <div className="flex items-center gap-2.5 text-sm text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{selectedUser.email}</div>
                  )}
                  <div className="flex items-center gap-2.5 text-sm text-slate-600"><Calendar className="w-4 h-4 text-slate-400" />Joined {formatDate(selectedUser.created_at)}</div>
                  <div className="flex items-center gap-2.5 text-sm text-slate-600"><Clock className="w-4 h-4 text-slate-400" />Last login: {selectedUser.last_login ? formatDateTime(selectedUser.last_login) : 'Never'}</div>
                </div>

                {/* Performance Stats */}
                {userStats && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Performance</h3>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Total Leads', value: userStats.total_leads, color: 'text-slate-900' },
                        { label: 'Active Leads', value: userStats.active_leads, color: 'text-blue-600' },
                        { label: 'Clients', value: userStats.clients, color: 'text-violet-600' },
                        { label: 'Active Deals', value: userStats.active_deals, color: 'text-amber-600' },
                        { label: 'Closed Deals', value: userStats.closed_deals, color: 'text-emerald-600' },
                        { label: 'Conversion', value: `${userStats.conversion_rate}%`, color: 'text-emerald-600' },
                        { label: 'Pending Tasks', value: userStats.pending_tasks, color: 'text-orange-600' },
                        { label: 'Completed', value: userStats.completed_tasks, color: 'text-emerald-600' },
                        { label: 'Follow-ups', value: userStats.followups_today, color: 'text-cyan-600' },
                      ].map(s => (
                        <div key={s.label} className="bg-white rounded-lg p-2.5 text-center border border-slate-100">
                          <p className={`text-lg font-bold ${s.color} tracking-tight`}>{s.value}</p>
                          <p className="text-[10px] text-slate-500 font-medium">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {canEditUser(selectedUser) && (
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start h-10" onClick={() => { setShowProfile(false); openEdit(selectedUser); }}><Edit2 className="w-4 h-4 mr-2" /> Edit Profile</Button>
                    <Button variant="outline" className="w-full justify-start h-10" onClick={() => handleResetPassword(selectedUser.id, selectedUser.name)}><RefreshCw className="w-4 h-4 mr-2" /> Reset Password</Button>
                    {(selectedUser.user_status || 'active') === 'active' ? (
                      <Button variant="outline" className="w-full justify-start h-10 text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setShowProfile(false); openDeactivate(selectedUser); }}><UserMinus className="w-4 h-4 mr-2" /> Deactivate User</Button>
                    ) : (
                      <Button variant="outline" className="w-full justify-start h-10 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => { handleActivate(selectedUser.id); setShowProfile(false); }}><UserCheck className="w-4 h-4 mr-2" /> Reactivate User</Button>
                    )}
                    {isSuperAdmin && selectedUser.id !== user.id && (
                      <Button variant="outline" className="w-full justify-start h-10 text-purple-600 border-purple-200 hover:bg-purple-50" onClick={() => handleImpersonate(selectedUser)}><LogIn className="w-4 h-4 mr-2" /> Login as {selectedUser.name}</Button>
                    )}
                  </div>
                )}
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* ══════════ DEACTIVATE DIALOG ══════════ */}
        <Dialog open={showDeactivate} onOpenChange={setShowDeactivate}>
          <DialogContent className="max-w-md rounded-xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Deactivate {selectedUser?.name}</DialogTitle></DialogHeader>
            {transferSummary && (
              <div className="mt-4 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800 mb-2">Records that will be transferred:</p>
                  <div className="grid grid-cols-2 gap-2 text-amber-700">
                    <span>Active Leads: <strong>{transferSummary.leads}</strong></span>
                    <span>Clients: <strong>{transferSummary.clients}</strong></span>
                    <span>Active Deals: <strong>{transferSummary.deals}</strong></span>
                    <span>Pending Tasks: <strong>{transferSummary.tasks}</strong></span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Transfer all records to *</Label>
                  <Select value={transferTo} onValueChange={setTransferTo}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="transfer-to-select"><SelectValue placeholder="Select a user..." /></SelectTrigger>
                    <SelectContent>
                      {activeUsersForTransfer.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_CONFIG[u.role]?.label})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500">Historical notes, calls, and activity logs will be preserved under {selectedUser?.name}&apos;s name.</p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setShowDeactivate(false)}>Cancel</Button>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleDeactivate} disabled={loading || !transferTo} data-testid="confirm-deactivate">
                    {loading ? 'Processing...' : 'Deactivate & Transfer'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ══════════ DELETE DIALOG ══════════ */}
        <Dialog open={showDelete} onOpenChange={v => { setShowDelete(v); if (!v) setDeleteConfirm(''); }}>
          <DialogContent className="max-w-sm rounded-xl">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Permanently Delete</DialogTitle></DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 font-medium">This action is irreversible.</p>
                <p className="text-xs text-red-600 mt-1">{selectedUser?.name} and all their data will be permanently removed.</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Type <strong>DELETE</strong> to confirm</Label>
                <Input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} className="mt-1 h-10 rounded-lg" placeholder="DELETE" data-testid="delete-confirm-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowDelete(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteUser} disabled={loading || deleteConfirm !== 'DELETE'} data-testid="confirm-delete">
                  {loading ? 'Deleting...' : 'Delete Permanently'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ══════════ AUDIT LOG SHEET ══════════ */}
        <Sheet open={showAudit} onOpenChange={setShowAudit}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader className="mb-4">
              <SheetTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-slate-400" /> Audit Log</SheetTitle>
            </SheetHeader>
            <div className="space-y-0">
              {auditLogs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No audit records yet</p>
              ) : auditLogs.map(log => (
                <div key={log.id} className="flex items-start gap-3 py-3 border-b border-slate-50 last:border-0">
                  <div className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${
                    log.action.includes('login') ? 'bg-blue-50' :
                    log.action.includes('created') ? 'bg-emerald-50' :
                    log.action.includes('deleted') || log.action.includes('deactivated') ? 'bg-red-50' :
                    log.action.includes('impersonation') ? 'bg-purple-50' :
                    'bg-slate-50'
                  }`}>
                    <Shield className={`w-3.5 h-3.5 ${
                      log.action.includes('login') ? 'text-blue-500' :
                      log.action.includes('created') ? 'text-emerald-500' :
                      log.action.includes('deleted') || log.action.includes('deactivated') ? 'text-red-500' :
                      log.action.includes('impersonation') ? 'text-purple-500' :
                      'text-slate-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{log.details}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                      <span>{formatDateTime(log.created_at)}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300" />
                      <span className="font-medium text-slate-500">{log.performed_by_name}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium">{log.action}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Temporary Password Dialog */}
        <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Password Reset Successful</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">
                New temporary password for <span className="font-semibold text-slate-900">{tempPassword?.name}</span>:
              </p>
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                <code className="flex-1 text-base font-mono font-bold text-slate-900 tracking-wide select-all" data-testid="temp-password-display">{tempPassword?.password}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(tempPassword?.password || '');
                    toast.success('Copied to clipboard');
                  }}
                  data-testid="copy-temp-password"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-amber-600">Share this password securely. It will not be shown again.</p>
              <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => setTempPassword(null)}>Done</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
};

export default UserManagement;
